import {
  type MouseEvent,
  type KeyboardEvent,
  forwardRef,
  type ReactNode,
  type HTMLAttributes,
  type TableHTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
  useState,
} from 'react'
import classnames from 'classnames'
import styled from 'styled-components'
import {checkTextOverflowed} from 'utils'
import {KEY_CODES} from 'definitions/constant'

const StyledResizeHandle = styled.div`
  position: absolute;
  top: 0;
  right: -3px;
  bottom: 0;
  width: 0.75rem;
  background: transparent;
  z-index: 1;
  cursor: col-resize;

  &::after {
    content: '';
    position: absolute;
    top: 25%;
    bottom: 25%;
    right: 3px;
    width: 1px;
    background: rgba(255, 255, 255, 0.6);
    transition: background 0.2s;
  }

  &:hover,
  &.active-resizing {
    &::after {
      background: rgba(255, 255, 255, 0.9);
    }
  }
`

/**
 * TypeScript Interfaces
 */

export interface TableProps extends TableHTMLAttributes<HTMLTableElement> {
  children: ReactNode
  className?: string
}

export interface TableHeaderProps
  extends HTMLAttributes<HTMLTableSectionElement> {
  children: ReactNode
  className?: string
}

export interface TableBodyProps
  extends HTMLAttributes<HTMLTableSectionElement> {
  children: ReactNode
  className?: string
}

export interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  children: ReactNode
  onClick?: () => void
  selected?: boolean
  className?: string
  tabIndex?: number
  onKeyDown?: (e: KeyboardEvent<HTMLTableRowElement>) => void
}

export interface TableHeaderCellProps
  extends ThHTMLAttributes<HTMLTableCellElement> {
  children: ReactNode
  width?: number
  resizable?: boolean
  onResizeStart?: (e: MouseEvent<HTMLDivElement>) => void
  className?: string
}

export interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  children: ReactNode
  align?: 'left' | 'center' | 'right'
  className?: string
  dataLabel?: string
  dataCy?: string
}

export interface ResizeHandleProps
  extends React.HTMLAttributes<HTMLDivElement> {
  isResizing: boolean
  onMouseDown: (e: MouseEvent<HTMLDivElement>) => void
  className?: string
}

/**
 * Components
 */

export const ResizeHandle = ({
  isResizing,
  onMouseDown,
  className,
}: ResizeHandleProps) => {
  return (
    <StyledResizeHandle
      data-cy="table-column-resize-handle"
      className={classnames(
        'group-hover:visible',
        {
          visible: isResizing,
          invisible: !isResizing,
        },
        className,
      )}
      onMouseDown={event => onMouseDown(event)}
      role="separator"
      aria-orientation="vertical"
    />
  )
}

export const Table = forwardRef<HTMLTableElement, TableProps>(
  ({children, className, ...props}, ref) => {
    return (
      <table
        ref={ref}
        className={classnames('table-fixed w-full', className)}
        {...props}
      >
        {children}
      </table>
    )
  },
)

Table.displayName = 'Table'

export const TableHeader = forwardRef<
  HTMLTableSectionElement,
  TableHeaderProps
>(({children, className, ...props}, ref) => {
  return (
    <thead
      ref={ref}
      className={classnames(
        'static text-gray-600 text-xs uppercase bg-dope-black group',
        className,
      )}
      {...props}
    >
      {children}
    </thead>
  )
})

TableHeader.displayName = 'TableHeader'

export const TableBody = forwardRef<HTMLTableSectionElement, TableBodyProps>(
  ({children, ...props}, ref) => {
    return (
      <tbody ref={ref} {...props}>
        {children}
      </tbody>
    )
  },
)

TableBody.displayName = 'TableBody'

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({children, onClick, selected, className, onKeyDown, ...props}, ref) => {
    const handleKeyDown = (e: KeyboardEvent<HTMLTableRowElement>) => {
      if (onClick && (e.key === KEY_CODES.ENTER || e.key === KEY_CODES.SPACE)) {
        e.preventDefault()
        onClick()
      }
      onKeyDown?.(e)
    }

    return (
      <tr
        ref={ref}
        className={classnames(
          {
            'cursor-default hover:bg-gray-800': !!onClick,
            'bg-gray-700': selected,
          },
          className,
        )}
        onClick={onClick}
        onKeyDown={handleKeyDown}
        aria-selected={selected}
        {...props}
        role="row"
      >
        {children}
      </tr>
    )
  },
)

TableRow.displayName = 'TableRow'

export const TableHeaderCell = forwardRef<
  HTMLTableCellElement,
  TableHeaderCellProps
>(({children, width, resizable, onResizeStart, className, ...props}, ref) => {
  const [isResizing, setIsResizing] = useState(false)

  const handleResizeStart = (e: MouseEvent<HTMLDivElement>) => {
    setIsResizing(true)
    onResizeStart?.(e)

    const handleMouseUp = () => {
      setIsResizing(false)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    document.addEventListener('mouseup', handleMouseUp)
  }

  const style = width ? {width: `${width}px`} : undefined

  return (
    <th
      ref={ref}
      className={classnames(
        'py-4 border-b-0.5 border-gray-600 truncate text-left bg-dope-black relative',
        {
          'pr-6': resizable,
        },
        className,
      )}
      style={style}
      {...props}
    >
      {children}
      {resizable && (
        <ResizeHandle isResizing={isResizing} onMouseDown={handleResizeStart} />
      )}
    </th>
  )
})

TableHeaderCell.displayName = 'TableHeaderCell'

export const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
  ({children, className, dataLabel, dataCy, ...props}, ref) => {
    return (
      <td
        ref={ref}
        className={classnames('h-9 text-xs text-gray-100 truncate', className)}
        data-label={dataLabel}
        data-cy={dataCy}
        onMouseOver={checkTextOverflowed}
        onFocus={checkTextOverflowed}
        {...props}
      >
        {children}
      </td>
    )
  },
)

TableCell.displayName = 'TableCell'
